import React from 'react';
import './DeleteAccountModal.css';

/**
 * The last thing between a user and permanent erasure.
 *
 * Design intent: the dialog has to make the *scale* and the *finality* concrete
 * before it will accept a click. So it itemises what actually gets destroyed
 * (rather than a vague "all your data"), states plainly that this is not
 * reversible, and requires the word DELETE to be typed — a deliberate friction
 * that a mis-click or a muscle-memory Enter cannot get through.
 */

/** Typed exactly, case-sensitive: an accident cannot produce this. */
const CONFIRM_WORD = 'DELETE';

const DESTROYED = [
  'Your profile, sign-in and settings',
  'Your trips, itineraries and saved places',
  'Your posts, comments, likes and bookmarks',
  'Your photos — removed from storage, not just hidden',
  'Your direct messages, for you and the people you wrote to',
  'Your conversations with the AI planner',
  'Your followers, friends and pending requests',
];

interface DeleteAccountModalProps {
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ onCancel, onConfirm }) => {
  const [confirmation, setConfirmation] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canDelete = confirmation === CONFIRM_WORD && !isDeleting;

  // Escape closes — but only while idle. Once the purge is running there is
  // nothing to go back to, and dismissing would hide its outcome.
  React.useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isDeleting) onCancel();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKey);
    };
  }, [isDeleting, onCancel]);

  const handleConfirm = async () => {
    if (!canDelete) return;
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (caught: any) {
      setError(caught?.message || 'We could not delete your account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="v2-delete-account" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
      <button
        type="button"
        className="v2-delete-account__backdrop"
        aria-label="Cancel account deletion"
        onClick={() => !isDeleting && onCancel()}
      />

      <section className="v2-delete-account__panel">
        <header className="v2-delete-account__header">
          <span className="v2-delete-account__icon" aria-hidden>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div>
            <h2 id="delete-account-title">Delete your account?</h2>
            <p className="v2-delete-account__lede">
              This is permanent. Your account and everything in it will be erased, and
              <strong> we cannot bring any of it back</strong> — not by support request, not from a
              backup.
            </p>
          </div>
        </header>

        <div className="v2-delete-account__body">
          <p className="v2-delete-account__section-label">What gets deleted</p>
          <ul className="v2-delete-account__list">
            {DESTROYED.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <p className="v2-delete-account__note">
            Trips that other people cloned or bought stay with them — those are their own copies,
            and deleting your account does not reach into another person's trips.
          </p>

          <p className="v2-delete-account__note v2-delete-account__note--alt">
            Looking for a pause instead? Suspending a subscription is reversible and leaves your
            data untouched. That option isn't built yet — deletion is the only thing this button
            does, and it is final.
          </p>

          <label className="v2-delete-account__confirm">
            <span>
              Type <strong>{CONFIRM_WORD}</strong> to confirm
            </span>
            <input
              type="text"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={CONFIRM_WORD}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={isDeleting}
              aria-describedby="delete-account-title"
            />
          </label>

          {error && <p className="v2-delete-account__error">{error}</p>}
        </div>

        <footer className="v2-delete-account__actions">
          <button
            type="button"
            className="v2-delete-account__cancel"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Keep my account
          </button>
          <button
            type="button"
            className="v2-delete-account__confirm-button"
            onClick={handleConfirm}
            disabled={!canDelete}
          >
            {isDeleting ? 'Deleting…' : 'Permanently delete'}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default DeleteAccountModal;
